import React, { useEffect, useState } from 'react';
import { ClipboardCheck, Save, RotateCcw } from 'lucide-react';
import api from '../../utils/api';
import './StaffAttendancePage.css';

const STATUS_OPTIONS = [
  { id: 'present',       label: 'Present',     color: '#16a34a' },
  { id: 'absent',        label: 'Absent',      color: '#dc2626' },
  { id: 'half-day',      label: 'Half day',    color: '#d97706' },
  { id: 'leave-paid',    label: 'Paid leave',  color: '#2563eb' },
  { id: 'leave-unpaid',  label: 'Unpaid leave',color: '#7c3aed' },
  { id: 'holiday',       label: 'Holiday',     color: '#0891b2' }
];

export default function StaffAttendancePage() {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const load = async (forDate) => {
    setLoading(true);
    setMessage(null);
    try {
      const { data } = await api.get('/staff-attendance', { params: { date: forDate } });
      setStaff(data.staff || []);
    } catch (e) {
      setMessage({ type: 'error', text: e?.response?.data?.message || 'Failed to load staff' });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(date); /* eslint-disable-next-line */ }, []);

  const setStatus = (teacherId, status) => {
    setStaff((prev) => prev.map((s) => s.teacherId === teacherId ? { ...s, status } : s));
  };
  const setRemarks = (teacherId, remarks) => {
    setStaff((prev) => prev.map((s) => s.teacherId === teacherId ? { ...s, remarks } : s));
  };

  const markAll = (status) => setStaff((prev) => prev.map((s) => ({ ...s, status })));

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const records = staff
        .filter((s) => s.status)
        .map((s) => ({ teacherId: s.teacherId, status: s.status, remarks: s.remarks || '' }));
      if (!records.length) {
        setMessage({ type: 'warn', text: 'Pick a status for at least one teacher.' });
        return;
      }
      const { data } = await api.post('/staff-attendance/bulk', { date, records });
      setMessage({ type: 'ok', text: `Saved attendance for ${data.count} staff` });
    } catch (e) {
      setMessage({ type: 'error', text: e?.response?.data?.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const stats = STATUS_OPTIONS.map((opt) => ({
    ...opt,
    count: staff.filter((s) => s.status === opt.id).length
  }));

  return (
    <div className="sa-page">
      <header className="sa-head">
        <div>
          <h1 className="sa-title"><ClipboardCheck size={20} /> Staff attendance</h1>
          <p className="sa-sub">Mark teachers' attendance for the day. Used by the payroll engine for monthly pro-rata calculations.</p>
        </div>
        <div className="sa-controls">
          <label className="sa-date-label">
            Date
            <input type="date" value={date} onChange={(e) => { setDate(e.target.value); load(e.target.value); }} />
          </label>
        </div>
      </header>

      {message && <div className={`sa-msg sa-msg-${message.type}`}>{message.text}</div>}

      <div className="sa-stats">
        {stats.map((s) => (
          <div key={s.id} className="sa-stat" style={{ borderColor: s.color }}>
            <div className="sa-stat-num" style={{ color: s.color }}>{s.count}</div>
            <div className="sa-stat-lbl">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="sa-bulk">
        <span className="sa-bulk-label">Quick mark all as:</span>
        {STATUS_OPTIONS.map((opt) => (
          <button key={opt.id} className="sa-bulk-btn" style={{ borderColor: opt.color, color: opt.color }} onClick={() => markAll(opt.id)}>
            {opt.label}
          </button>
        ))}
        <button className="sa-bulk-btn sa-bulk-clear" onClick={() => markAll(null)}>
          <RotateCcw size={12} /> Clear
        </button>
      </div>

      {loading ? (
        <div className="sa-loading">Loading staff…</div>
      ) : staff.length === 0 ? (
        <div className="sa-empty">No active staff in this school.</div>
      ) : (
        <div className="sa-card">
          <table className="sa-table">
            <thead>
              <tr>
                <th>Emp ID</th>
                <th>Name</th>
                <th>Status</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {staff.map((s) => (
                <tr key={s.teacherId}>
                  <td className="sa-id">{s.employeeId}</td>
                  <td className="sa-name">{s.firstName} {s.lastName}</td>
                  <td>
                    <div className="sa-status-row">
                      {STATUS_OPTIONS.map((opt) => (
                        <button
                          key={opt.id}
                          className={`sa-status-btn ${s.status === opt.id ? 'on' : ''}`}
                          style={s.status === opt.id ? { background: opt.color, color: '#fff', borderColor: opt.color } : { color: opt.color, borderColor: opt.color + '40' }}
                          onClick={() => setStatus(s.teacherId, opt.id)}
                        >
                          {opt.label.split(' ')[0]}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td>
                    <input
                      type="text"
                      className="sa-remarks"
                      placeholder="Optional note"
                      value={s.remarks || ''}
                      onChange={(e) => setRemarks(s.teacherId, e.target.value)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="sa-footer">
        <button className="sa-save" onClick={save} disabled={saving || staff.length === 0}>
          <Save size={15} /> {saving ? 'Saving…' : 'Save attendance'}
        </button>
      </div>
    </div>
  );
}
